/* © Kwazar. All rights reserved. Proprietary theme code — copying, redistribution or resale is prohibited. */
import { emit, formatMoney, toPresentment } from '@mx/core';

/** Same math as snippets/offer-calc.liquid — keep both in sync. */
export function applyDiscount(subtotal, type, value, prices = []) {
  let total = subtotal;
  if (type === 'percent') total = Math.round((subtotal * (100 - value)) / 100);
  // Amounts are typed in the store currency: convert them for visitors browsing in another currency.
  else if (type === 'amount') total = subtotal - toPresentment(Math.round(value * 100));
  else if (type === 'fixed') total = toPresentment(Math.round(value * 100));
  // Free units (Buy X get Y free): the cheapest units are free — exact in every currency.
  else if (type === 'free') total = subtotal - [...prices].sort((a, b) => a - b).slice(0, Math.max(0, Math.floor(value))).reduce((a, b) => a + b, 0);
  return Math.max(0, total);
}

function fill(tpl, v) {
  const saved = Math.max(0, v.compare - v.total);
  return tpl
    .replaceAll('{qty}', v.qty)
    .replaceAll('{price}', formatMoney(v.total))
    .replaceAll('{compare_price}', formatMoney(v.compare))
    .replaceAll('{unit_price}', formatMoney(Math.round(v.total / v.qty)))
    .replaceAll('{unit_compare_price}', formatMoney(Math.round(v.compare / v.qty)))
    .replaceAll('{saved}', formatMoney(saved))
    .replaceAll('{saved_rounded}', formatMoney(Math.ceil(saved / 100) * 100))
    .replaceAll('{saved_percent}', `${v.compare ? Math.round((saved / v.compare) * 100) : 0}%`)
    .replaceAll('{title}', v.title || '')
    .replaceAll('{variant}', v.variant || '');
}

class MxOffers extends HTMLElement {
  async connectedCallback() {
    await customElements.whenDefined('mx-product');
    this.product = this.closest('mx-product');
    this.offers = [...this.querySelectorAll('[data-offer]')];
    this.addons = [...this.querySelectorAll('[data-addon]')];
    this.perItem = this.dataset.perItem === 'true';
    this.touched = new WeakSet();
    this.setupExtras();
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-unit-picker]')) { this.touched.add(e.target); this.syncPickerImage(e.target, true); return this.refresh(); }
      if (e.target.matches('.mx-addon__check')) return this.refresh();
      const offer = e.target.closest('[data-offer]');
      if (offer && e.target.type === 'radio') this.select(offer);
    });
    this.product?.addEventListener('mx-variant-change', (e) => this.onVariant(e.detail.variant));
    this.addEventListener('shopify:block:select', (e) => { const o = e.target.closest('[data-offer]'); if (o) this.select(o); });
    const initial = this.offers.find((o) => o.querySelector('input:checked')) || null;
    if (initial) this.select(initial, false); else this.refresh();
  }

  hasSelection() { return !!this.selected; }

  /** Labels above unit pickers, gift cards row, and remembered home of add-ons shown inside offers. */
  setupExtras() {
    this.style.setProperty('--o-count', this.offers.length);
    const num = this.dataset.pickersNumber || '#{index}';
    this.querySelectorAll('.mx-offer__picker-index').forEach((el, k, all) => {
      const i = [...el.closest('[data-pickers]').querySelectorAll('.mx-offer__picker-index')].indexOf(el) + 1;
      el.textContent = num.includes('{index}') ? num.replace('{index}', i) : `${num}${i}`;
    });
    if (this.dataset.pickersSkip === 'true') this.querySelectorAll('[data-unit-picker] option[disabled]').forEach((o) => o.remove());
    if (this.dataset.pickersStyle === 'options') this.querySelectorAll('[data-unit-picker]').forEach((s) => this.buildOptionPickers(s));
    const label = this.querySelector(':scope > template[data-pickers-label]');
    if (label) this.querySelectorAll('[data-pickers]').forEach((p) => {
      const l = document.createElement('span'); l.className = 'mx-offer__pickers-label'; l.innerHTML = label.innerHTML; p.prepend(l);
    });
    this.addons.forEach((a) => { a._home = a.parentElement; a._next = a.nextSibling; });
    const gifts = this.querySelector('[data-gifts]');
    const cards = this.addons.filter((a) => a.dataset.display === 'card');
    if (gifts && cards.length) {
      gifts.hidden = false;
      cards.forEach((a) => {
        a.hidden = true;
        const card = document.createElement('div');
        card.className = 'mx-gift is-locked';
        const value = a.querySelector('.mx-addon__price s')?.textContent || '';
        card.innerHTML = `${a.dataset.cardBadge ? `<span class="mx-gift__badge">${a.dataset.cardBadge}</span>` : ''}${a.dataset.img ? `<img class="mx-gift__img" src="${a.dataset.img}" alt="" loading="lazy">` : ''}<span class="mx-gift__lock" aria-hidden="true"><svg class="mx-icon" viewBox="0 0 24 24" style="--mx-icon-size:14px"><rect x="5" y="10.5" width="14" height="10" rx="2"/><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3"/></svg></span><span class="mx-gift__title">${a.dataset.title || ''}</span>${value ? `<span class="mx-gift__value"><s>${value}</s></span>` : ''}`;
        a._card = card;
        gifts.appendChild(card);
      });
    }
  }

  /** "One dropdown per option": the variant select stays (hidden) as the source of truth; one select per option drives it. */
  buildOptionPickers(unit) {
    const names = (unit.dataset.optionNames || '').split('|').filter(Boolean);
    if (names.length < 2 || unit._opts) return;
    const variants = [...unit.options].map((o) => ({ id: o.value, opts: (o.dataset.opts || '').split('|'), available: !o.disabled }));
    const wrap = document.createElement('span');
    wrap.className = 'mx-offer__opt-pickers';
    unit._opts = names.map((name, k) => {
      const sel = document.createElement('select');
      sel.className = 'mx-select mx-offer__opt-picker';
      sel.dataset.optPicker = k;
      sel.setAttribute('aria-label', `${name} — ${unit.getAttribute('aria-label') || ''}`);
      const values = [...new Set(variants.map((v) => v.opts[k]).filter(Boolean))];
      const group = document.createElement('optgroup');
      group.label = name;
      values.forEach((val) => { const o = document.createElement('option'); o.value = val; o.textContent = val; group.appendChild(o); });
      sel.appendChild(group);
      wrap.appendChild(sel);
      sel.addEventListener('change', (e) => {
        e.stopPropagation();
        const want = unit._opts.map((x) => x.value);
        let match = variants.find((v) => v.opts.every((o, i) => o === want[i]));
        if (!match || !match.available) match = variants.find((v) => v.available && v.opts[k] === sel.value) || match;
        if (!match) return;
        unit.value = match.id;
        this.syncOptionPickers(unit);
        unit.dispatchEvent(new Event('change', { bubbles: true }));
      });
      return sel;
    });
    unit._variants = variants;
    unit.classList.add('mx-offer__unit-hidden');
    unit.after(wrap);
    unit.closest('.mx-offer__picker')?.classList.add('mx-offer__picker--opts');
    this.syncOptionPickers(unit);
  }

  syncOptionPickers(unit) {
    if (!unit._opts) return;
    const current = unit._variants.find((v) => v.id === unit.value) || unit._variants[0];
    unit._opts.forEach((sel, k) => {
      sel.value = current.opts[k];
      // Grey out values that have no available variant with the other current choices.
      [...sel.options].forEach((o) => {
        const ok = unit._variants.some((v) => v.available && v.opts[k] === o.value && v.opts.every((x, i) => i === k || x === current.opts[i]));
        o.textContent = o.value + (ok ? '' : ` — ${(window.KwazarStrings || {}).sold_out || 'Sold out'}`);
        o.disabled = !ok && this.dataset.pickersSkip === 'true' ? true : false;
      });
    });
  }

  syncPickerImage(select, fromUser = false) {
    const opt = select.selectedOptions[0];
    const img = select.parentElement.querySelector('[data-picker-img]');
    if (img && opt?.dataset.img) img.src = opt.dataset.img;
    // Stacked offer images follow each unit's chosen variant.
    const offer = select.closest('[data-offer]') || this.selected;
    const pickers = [...(offer?._extra || offer)?.querySelectorAll('[data-unit-picker]') || []];
    const k = pickers.indexOf(select);
    const auto = offer?.querySelectorAll('[data-offer-auto-img] img')[k];
    if (auto && opt?.dataset.img) { auto.src = opt.dataset.img; auto.removeAttribute('srcset'); }
    if (fromUser && this.dataset.pickersGallery === 'true' && opt?.dataset.media) this.product?.querySelector('mx-gallery')?.goToMedia?.(opt.dataset.media);
  }

  placeAddons(units) {
    this.addons.forEach((a) => {
      const unlocked = units >= Number(a.dataset.minUnits);
      if (a.dataset.display === 'inside') {
        if (unlocked && this.selected) {
          let extra = this.selected._extra || this.selected.querySelector('.mx-offer__extra');
          if (!extra) { extra = document.createElement('span'); extra.className = 'mx-offer__extra'; this.selected.appendChild(extra); this.moveExtras(this.selected); }
          if (a.parentElement !== extra) extra.appendChild(a);
        } else if (a.parentElement !== a._home) a._home.insertBefore(a, a._next && a._next.parentNode === a._home ? a._next : null);
      }
      if (a._card) {
        const was = a._card.classList.contains('is-locked');
        a._card.classList.toggle('is-locked', !unlocked);
        if (was && unlocked) { a._card.classList.remove('is-unlocking'); void a._card.offsetWidth; a._card.classList.add('is-unlocking'); }
      }
    });
  }


  /** Vertical cards are narrow: the selected card's extra content (unit pickers, pack, gifts) is shown under the cards. */
  moveExtras(offer) {
    if (!this.matches('.mx-offers--vertical, .mx-offers--compact')) return;
    if (!this.below) { this.below = document.createElement('div'); this.below.className = 'mx-offers__below'; this.querySelector('.mx-offers__list').after(this.below); }
    this.offers.forEach((o) => { if (o._extra && o._extra.parentElement === this.below && o !== offer) o.appendChild(o._extra); });
    const extra = offer.querySelector(':scope > .mx-offer__extra') || offer._extra;
    if (extra) { offer._extra = extra; this.below.appendChild(extra); }
  }

  select(offer, track = true) {
    this.selected = offer;
    this.moveExtras(offer);
    this.offers.forEach((o) => {
      const on = o === offer;
      o.classList.toggle('is-selected', on);
      o.querySelector('input[type="radio"]').checked = on;
    });
    this.refresh();
    if (track) emit('offer:select', { stack: this.dataset.stack, offer: offer.dataset.label, qty: Number(offer.dataset.qty), total: this.state?.total });
  }

  /** Main variant changed: re-price, and move untouched unit pickers to the new variant. */
  onVariant(variant) {
    if (!variant) return;
    this.dataset.variantPrice = variant.price;
    this.dataset.variantCompare = variant.compare_at_price || 0;
    this.querySelectorAll('[data-unit-picker]').forEach((s) => { if (!this.touched.has(s)) { s.value = variant.id; this.syncPickerImage(s); this.syncOptionPickers(s); } });
    if (variant.image) this.querySelectorAll('[data-offer-auto-img] img').forEach((img) => { img.src = variant.image; img.removeAttribute('srcset'); });
    this.refresh();
  }

  variantInfo(id) {
    const v = this.product?.data.variants.find((x) => x.id === Number(id));
    return v ? { price: v.price, compare: Math.max(v.compare_at_price || 0, v.price), title: v.title } : null;
  }

  /** Units of the main product for one offer: per-unit pickers when enabled, else the main variant. */
  mainUnits(offer, count) {
    const mainId = Number(this.product?.variant?.id);
    const pickers = [offer, offer._extra].filter((el) => el && (el === offer || !offer.contains(el))).flatMap((el) => [...el.querySelectorAll('[data-unit-picker]')]);
    if (this.perItem && pickers.length === count) return pickers.map((s) => Number(s.value));
    return Array(count).fill(mainId);
  }

  compute(offer) {
    const base = offer.dataset.base;
    const unitCompare = (info) => (base === 'compare_at' ? info.compare : info.price);
    const type = offer.dataset.type;
    let subtotal = 0, compare = 0, qty = 0;
    const lines = [];
    const prices = [];
    const addMain = (count) => {
      for (const id of this.mainUnits(offer, count)) {
        const info = this.variantInfo(id) || { price: Number(this.dataset.variantPrice), compare: Number(this.dataset.variantCompare) };
        prices.push(info.price);
        subtotal += info.price; compare += unitCompare(info); qty += 1; lines.push({ id, quantity: 1 });
      }
    };
    if (type === 'tier') {
      addMain(Number(offer.dataset.qty));
      const total = applyDiscount(subtotal, offer.dataset.dtype, Number(offer.dataset.dvalue), prices);
      return { total, compare: Math.max(compare, total), qty, lines, units: qty };
    }
    if (type === 'pack') {
      addMain(Number(offer.dataset.mainQty));
      const units = qty;
      for (const entry of (offer.dataset.items || '').split(',').filter(Boolean)) {
        const [id, price, cmp] = entry.split(':').map(Number);
        subtotal += price; compare += base === 'compare_at' ? cmp : price; qty += 1; lines.push({ id, quantity: 1 });
      }
      const total = applyDiscount(subtotal, offer.dataset.dtype, Number(offer.dataset.dvalue));
      return { total, compare: Math.max(compare, total), qty, lines, units };
    }
    if (type === 'bxgy') {
      const buy = Number(offer.dataset.buy), get = Number(offer.dataset.get);
      const same = offer.dataset.same === 'true';
      if (same) {
        const ids = this.mainUnits(offer, buy + get);
        ids.forEach((id, i) => {
          const info = this.variantInfo(id);
          const price = i < buy ? info.price : Math.round((info.price * (100 - Number(offer.dataset.getDiscount))) / 100);
          subtotal += price; compare += unitCompare(info); lines.push({ id, quantity: 1 });
        });
        qty = buy + get;
      } else {
        addMain(buy);
        const gPrice = Number(offer.dataset.getPrice), gCompare = Number(offer.dataset.getCompare);
        subtotal += Math.round((gPrice * get * (100 - Number(offer.dataset.getDiscount))) / 100);
        compare += gCompare * get; qty += get;
        lines.push({ id: Number(offer.dataset.getVariant), quantity: get });
      }
      return { total: subtotal, compare: Math.max(compare, subtotal), qty, lines, units: same ? buy + get : buy };
    }
    return { total: 0, compare: 0, qty: 0, lines, units: 0 };
  }

  refresh() {
    const main = this.product?.variant;
    this.offers.forEach((offer) => {
      const r = this.compute(offer);
      const values = { ...r, title: this.product?.data.title, variant: main?.title };
      offer.querySelectorAll('[data-tpl]').forEach((el) => { el.innerHTML = fill(el.dataset.tpl, values); });
      const cmp = offer.querySelector('.mx-offer__compare');
      if (cmp) cmp.hidden = r.compare <= r.total;
      offer._result = r;
    });
    const units = this.selected?._result?.units || 0;
    this.placeAddons(units);
    let addonTotal = 0, addonCompare = 0;
    this.addons.forEach((a) => {
      const unlocked = units >= Number(a.dataset.minUnits);
      a.classList.toggle('is-locked', !unlocked);
      const check = a.querySelector('.mx-addon__check');
      if (check) check.disabled = !unlocked;
      const active = unlocked && (a.dataset.mode === 'gift' || check?.checked);
      a.classList.toggle('is-active', active);
      if (active) { addonTotal += Number(a.dataset.price); addonCompare += Number(a.dataset.value); }
    });
    const r = this.selected?._result;
    this.state = r ? { total: r.total + addonTotal, compare: r.compare + addonCompare, qty: r.qty, label: this.selected.dataset.label } : null;
    if (this.state) this.product?.dispatchEvent(new CustomEvent('mx-offer-change', { detail: this.state }));
  }

  getItems() {
    if (!this.selected) return null;
    const r = this.selected._result || this.compute(this.selected);
    const tag = `${this.dataset.stack || 'Offer'} — ${this.selected.dataset.label}`.replace(/<[^>]+>/g, '');
    const items = r.lines.map((l) => ({ ...l, properties: { _offer: tag } }));
    // Free gifts chosen on this offer (one each).
    this.selected.querySelectorAll('[data-offer-gift]:not([data-unavailable])').forEach((g) => items.push({ id: Number(g.dataset.offerGift), quantity: 1, properties: { _offer: tag, _addon: 'gift' } }));
    this.addons.forEach((a) => {
      if (r.units < Number(a.dataset.minUnits)) return;
      const check = a.querySelector('.mx-addon__check');
      if (a.dataset.mode === 'gift' || check?.checked) items.push({ id: Number(a.dataset.variant), quantity: 1, properties: { _offer: tag, _addon: a.dataset.mode } });
    });
    return items;
  }
}
if (!customElements.get('mx-offers')) customElements.define('mx-offers', MxOffers);
